<template>
  <div class="tool-form__overlay" @click.self="$emit('close')">
    <div class="tool-form">
      <div class="tool-form__header">
        <h3>{{ isEditing ? 'Editar Ferramenta' : 'Nova Ferramenta' }}</h3>
        <button class="tool-form__close" @click="$emit('close')">&times;</button>
      </div>

      <div class="tool-form__body">
        <label class="tool-form__label">
          Código (nome)
          <input v-model="form.nome" class="tool-form__input" placeholder="Ex: 1.2.3" />
        </label>

        <label class="tool-form__label">
          Descrição
          <input v-model="form.descricao" class="tool-form__input" placeholder="Nome da ferramenta" />
        </label>

        <label class="tool-form__label">
          Nível
          <select v-model.number="form.nivel" class="tool-form__input">
            <option :value="0">0 — Pilar</option>
            <option :value="1">1 — Subgrupo</option>
            <option :value="2">2 — Ferramenta</option>
          </select>
        </label>

        <label class="tool-form__label">
          Pai (ferramenta_pai_id)
          <select v-model="form.ferramenta_pai_id" class="tool-form__input">
            <option :value="null">— Nenhum (raiz) —</option>
            <option
              v-for="opt in parentOptions"
              :key="opt.id"
              :value="opt.id"
            >{{ opt.nome }}. {{ opt.descricao }} (nível {{ opt.nivel }})</option>
          </select>
        </label>

        <label class="tool-form__label">
          Idioma
          <select v-model="form.idioma" class="tool-form__input">
            <option value="pt">Português</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="nl">Nederlands</option>
          </select>
        </label>
      </div>

      <div class="tool-form__footer">
        <button
          v-if="isEditing"
          class="tool-form__btn tool-form__btn--danger"
          @click="handleDelete"
          :disabled="saving"
        >Excluir</button>
        <div class="tool-form__spacer"></div>
        <button class="tool-form__btn" @click="$emit('close')">Cancelar</button>
        <button
          class="tool-form__btn tool-form__btn--primary"
          @click="handleSave"
          :disabled="saving"
        >{{ saving ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import api from '@/api';

export default {
  name: 'ToolFormModal',
  props: {
    ferramenta: { type: Object, default: null },
    allFerramentas: { type: Array, default: () => [] },
    defaultLang: { type: String, default: 'pt' },
  },
  emits: ['close', 'saved', 'deleted'],
  setup(props, { emit }) {
    const isEditing = computed(() => !!props.ferramenta);
    const saving = ref(false);

    const form = reactive({
      nome: props.ferramenta?.nome || '',
      descricao: props.ferramenta?.descricao || '',
      nivel: props.ferramenta?.nivel ?? 0,
      ferramenta_pai_id: props.ferramenta?.ferramenta_pai_id || null,
      idioma: props.ferramenta?.idioma || props.defaultLang,
    });

    const parentOptions = computed(() =>
      props.allFerramentas
        .filter(f => f.nivel < 2 && (!props.ferramenta || f.id !== props.ferramenta.id))
        .sort((a, b) => a.nome.localeCompare(b.nome))
    );

    const handleSave = async () => {
      if (!form.nome || !form.descricao) return;
      saving.value = true;
      try {
        if (isEditing.value) {
          await api.updateFerramenta(props.ferramenta.id, { ...form });
        } else {
          await api.createFerramenta({ ...form });
        }
        emit('saved');
      } catch (err) {
        alert('Erro: ' + (err.response?.data?.message || err.message));
      } finally {
        saving.value = false;
      }
    };

    const handleDelete = async () => {
      if (!confirm('Tem certeza que deseja excluir esta ferramenta?')) return;
      saving.value = true;
      try {
        await api.deleteFerramenta(props.ferramenta.id);
        emit('deleted');
      } catch (err) {
        alert('Erro ao excluir: ' + (err.response?.data?.message || err.message));
      } finally {
        saving.value = false;
      }
    };

    return { form, isEditing, saving, parentOptions, handleSave, handleDelete };
  },
};
</script>

<style scoped>
.tool-form__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tool-form {
  background: #fff;
  border-radius: 8px;
  width: 480px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}

.tool-form__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.tool-form__header h3 { margin: 0; font-size: 17px; }

.tool-form__close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.tool-form__body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tool-form__label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
}

.tool-form__input {
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-weight: 400;
  color: #333;
}

.tool-form__input:focus {
  outline: none;
  border-color: #1a73e8;
}

.tool-form__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #eee;
}

.tool-form__spacer { flex: 1; }

.tool-form__btn {
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  background: #f0f0f0;
  color: #333;
}

.tool-form__btn:hover { background: #e0e0e0; }

.tool-form__btn--primary {
  background: #1a73e8;
  color: #fff;
  border-color: #1a73e8;
}

.tool-form__btn--primary:hover { background: #1557b0; }

.tool-form__btn--primary:disabled {
  background: #93b4e8;
  cursor: not-allowed;
}

.tool-form__btn--danger {
  background: #d93025;
  color: #fff;
  border-color: #d93025;
}

.tool-form__btn--danger:hover { background: #b7271d; }

.tool-form__btn--danger:disabled {
  background: #e8a09b;
  cursor: not-allowed;
}
</style>
